import Vue from 'vue';
import { getApiErrorMessage } from '../utils/apiErrorUtils';
import {
  CHART_OF_ACCOUNT_API,
  CHART_OF_ACCOUNT_MESSAGES,
  ERROR_MESSAGES,
} from '../utils/constants';
import {
  chartOfAccountBulkRows,
  chartOfAccountErrorParts,
  chartOfAccountWriteBody,
} from '../utils/chartOfAccountApi';

function getAxios() {
  return Vue.axios;
}

function failureFromBody(body, fallback) {
  const parts = chartOfAccountErrorParts(body?.data);
  const message = (typeof body?.message === 'string' && body.message.trim())
    ? body.message
    : fallback;
  return { ok: false, message, fieldErrors: parts.fieldErrors, rowErrors: parts.rowErrors };
}

function failureFromError(error, fallback) {
  const parts = chartOfAccountErrorParts(error?.response?.data?.data);
  return {
    ok: false,
    message: getApiErrorMessage(error, fallback),
    fieldErrors: parts.fieldErrors,
    rowErrors: parts.rowErrors,
  };
}

export default {
  namespaced: true,

  actions: {
    /**
     * Create or update one Chart of Accounts row.
     * Field errors (HTTP 400 data) are returned for the form and are not toasted.
     */
    async saveChartOfAccount({ commit }, { id, account } = {}) {
      const isEdit = Boolean(id);
      const url = isEdit ? CHART_OF_ACCOUNT_API.EDIT(id) : CHART_OF_ACCOUNT_API.ADD;
      const fallback = CHART_OF_ACCOUNT_MESSAGES.SAVE_FAILED;
      try {
        const response = await getAxios().post(url, { data: chartOfAccountWriteBody(account) });
        const body = response?.data || {};
        if (body.status !== 'success') {
          const result = failureFromBody(body, fallback);
          if (!result.fieldErrors) commit('sbError', result.message, { root: true });
          return result;
        }
        commit(
          'sbSuccess',
          isEdit ? CHART_OF_ACCOUNT_MESSAGES.UPDATED : CHART_OF_ACCOUNT_MESSAGES.ADDED,
          { root: true }
        );
        return { ok: true, fieldErrors: null, rowErrors: null };
      } catch (error) {
        console.error('[ChartOfAccountStore]: saveChartOfAccount error', error);
        const result = failureFromError(error, fallback);
        if (!result.fieldErrors) commit('sbError', result.message, { root: true });
        return result;
      }
    },

    /**
     * Bulk-create Chart of Accounts rows.
     * Row errors are returned for the preview table. The caller shows them;
     * this action does not toast the same 400.
     */
    async bulkAddChartOfAccounts({ commit }, rows) {
      const fallback = ERROR_MESSAGES.ERROR_UPLOADING_CHART_OF_ACCOUNT;
      try {
        const response = await getAxios().post(CHART_OF_ACCOUNT_API.BULK_ADD, {
          data: chartOfAccountBulkRows(rows),
        });
        const body = response?.data || {};
        if (body.status !== 'success') return failureFromBody(body, fallback);
        commit('sbSuccess', ERROR_MESSAGES.CHART_OF_ACCOUNT_UPLOADED, { root: true });
        return { ok: true, fieldErrors: null, rowErrors: null, message: body.message || '' };
      } catch (error) {
        console.error('[ChartOfAccountStore]: bulkAddChartOfAccounts error', error);
        return failureFromError(error, fallback);
      }
    },
  },
};
